import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  Optional,
} from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatChipsModule, MatChipSelectionChange } from '@angular/material/chips'
import { TranslateModule } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { AnalyticsService } from '../../../_services/analytics.service';

import { MEDIA_BELOW_MD } from '../../../_helpers/breakpoints';
import { DictationCopilotMeta, Note } from '../../../_interfaces/dictation';
import { TranscriptUtterance } from '../../../_helpers/transcription';
import { TranscriptListComponent } from '../../../shared/components/transcript-list/transcript-list.component';
import type { CollapsibleBoxClasses } from '../../../shared/components/collapsible-box/collapsible-box.component';
import { CopilotDetailsSectionsComponent } from '../../../shared/components/copilot-details-sections/copilot-details-sections.component';
import {
  CollapsibleListClasses,
  CollapsibleListComponent,
} from '../../../shared/components/collapsible-list/collapsible-list.component';
import {
  BottomSheetComponent,
  BottomSheetState,
} from '../../../shared/components/bottom-sheet/bottom-sheet.component';
import { SyncAllBannerComponent } from '../sync-all-banner/sync-all-banner.component';
import { ScrollToBottomDirective } from '../../../shared/directives/scroll-to-bottom.directive';

export const CONTENT_PANEL_COLLAPSED_HEIGHT_PX = 72;
export const CONTENT_PANEL_COLLAPSED_STORAGE_KEY = 'content_panel_collapsed';

export type ContentPanelView = 'transcript' | 'context';

@Component({
  selector: 'app-content-panel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatTabsModule,
    MatChipsModule,
    TranslateModule,
    TranscriptListComponent,
    CopilotDetailsSectionsComponent,
    CollapsibleListComponent,
    BottomSheetComponent,
    SyncAllBannerComponent,
    ScrollToBottomDirective,
  ],
  templateUrl: './content-panel.component.html',
  host: {
    '[class]': 'hostClasses',
  },
})
export class ContentPanelComponent implements OnInit, OnDestroy {
  @Input() utterances: TranscriptUtterance[] = [];
  @Input() notes: Note[] = [];
  @Input({ required: true }) copilot!: DictationCopilotMeta;
  @Input() showSyncAllBanner = false;
  @Input() syncAllDisabled = false;
  @Input() transcriptDeleted = false;
  @Input() contextReadonly = false;
  @Input() hideContextNotes = false;
  @Input() isLegalOrg = false;

  @Output() readonly saveNote = new EventEmitter<string>();
  @Output() readonly deleteNote = new EventEmitter<number>();
  @Output() readonly syncAll = new EventEmitter<void>();
  @Output() readonly collapsed = new EventEmitter<void>();

  @ViewChild(BottomSheetComponent) private bottomSheet?: BottomSheetComponent;

  selectedMobileView: ContentPanelView = 'context';
  selectedDesktopView: ContentPanelView = 'transcript';

  readonly collapsedHeightPx = CONTENT_PANEL_COLLAPSED_HEIGHT_PX;
  isMobileSheet = false;
  sheetState: BottomSheetState = 'collapsed';

  desktopCollapsed = true;

  readonly addedContextClasses: CollapsibleListClasses = {
    title: 'body-medium !font-normal text-on-surface-variant',
    itemText: 'body-medium whitespace-pre-wrap',
    itemAlt: 'bg-surface-container-lowest',
    empty: 'body-medium !font-normal text-outline',
  };

  readonly detailsBoxClasses: CollapsibleBoxClasses = {
    title: this.addedContextClasses.title,
  };

  readonly detailsPlaceholderClasses = this.addedContextClasses.empty ?? '';
  readonly detailsFieldClasses = this.addedContextClasses.itemText ?? 'body-medium';

  noteDraft = '';

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly breakpointObserver: BreakpointObserver,
    @Optional() private analytics: AnalyticsService | null = null,) {}

  get hostClasses(): string {
    const base =
      'min-h-0 flex-col border border-gray-200 border-(--color-linear-stroke) bg-white overflow-hidden shrink-0';

    if (this.isMobileSheet) {
      return 'contents';
    }

    const width = this.desktopCollapsed ? 'w-12' : 'w-[360px]';

    return `${base} h-full flex md:flex flex-col rounded-lg ${width} transition-[width,background-color] duration-300 ease-in-out motion-reduce:transition-none`;
  }

  get contextItems(): string[] {
    return this.notes.map((note) => note.note);
  }

  get isExpanded(): boolean {
    return this.sheetState === 'expanded';
  }

  ngOnInit(): void {
    this.desktopCollapsed = this.readCollapsedState();

    this.applyMobileBreakpoint(this.breakpointObserver.isMatched(MEDIA_BELOW_MD));

    this.breakpointObserver
      .observe(MEDIA_BELOW_MD)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => this.applyMobileBreakpoint(state.matches));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onChipsChange(view: ContentPanelView | null | undefined): void {
    if (!view) return;
    if (view !== this.selectedMobileView)
        this.trackContentViewed(view);

    const collapsed = !this.isExpanded;
    this.selectedMobileView = view;

    if (collapsed) {
      this.bottomSheet?.expand();
    }
  }

  onSheetStateChange(state: BottomSheetState): void {
    this.sheetState = state;
  }

  keepSelected(event: MatChipSelectionChange): void {
    if (event.isUserInput && !event.selected) {
      event.source.select();
    }
  }

  desktopToggleCollapse() {
    this.setDesktopCollapsed(!this.desktopCollapsed);
  }

  onCollapsedIconClick(view: ContentPanelView): void {
    this.selectedDesktopView = view;
    this.setDesktopCollapsed(false);

    this.trackContentViewed(view);
  }

  onDesktopTabChange(index: number): void {
    const view: ContentPanelView = index === 0 ? 'transcript' : 'context';
    if (view === this.selectedDesktopView) return;
    this.selectedDesktopView = view;

    this.trackContentViewed(view);
  }

  onSave(): void {
    if (this.contextReadonly) return;
    const trimmed = this.noteDraft.trim();
    if (!trimmed) return;
    this.saveNote.emit(trimmed);
    this.noteDraft = '';
  }

  onNoteKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onSave();
    }
  }

  onRemoveFromList(index: number): void {
    if (this.contextReadonly) return;
    if (index < 0 || index >= this.notes.length) return;
    this.deleteNote.emit(index);
  }

  private applyMobileBreakpoint(isBelowMd: boolean): void {
    this.isMobileSheet = isBelowMd;

    if (!isBelowMd) {
      this.sheetState = 'collapsed';
    }
  }

  private setDesktopCollapsed(collapsed: boolean): void {
    if (this.desktopCollapsed === collapsed) return;
    this.desktopCollapsed = collapsed;
    this.persistCollapsedState();
  }

  private readCollapsedState(): boolean {
    if (typeof localStorage === 'undefined') return true;

    const stored = localStorage.getItem(CONTENT_PANEL_COLLAPSED_STORAGE_KEY);
    if (stored == null) return true;

    try {
      return JSON.parse(stored) === true;
    } catch {
      return true;
    }
  }

  private persistCollapsedState(): void {
    if (typeof localStorage === 'undefined') return;

    try {
      localStorage.setItem(
        CONTENT_PANEL_COLLAPSED_STORAGE_KEY,
        JSON.stringify(this.desktopCollapsed),
      );
    } catch (error) {
      console.error('Failed to persist content panel collapsed state', error);
    }
  }

  // Analytics Helper

  private trackContentViewed(view: ContentPanelView) {
    this.analytics?.track(
      view === 'context'? 'context_viewed' : 'transcript_viewed',
      {surface: 'document_screen'});
  }
}
